import { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { useTabs } from '../../../../context/TabsContext';
import { hasAnyConditions } from '../../shared/ConditionsDropdown/utils/conditionHelpers';

// Default node dimensions for fallback
const NODE_WIDTH = 290;
const NODE_HEIGHT = 80;

/**
 * In-memory store for viewport positions per tab (session-only).
 * Keyed by tabId so two tabs on the same campaign keep independent pan/zoom.
 * Value: { pan, zoom, campaignId } — restore only when campaign still matches.
 */
const viewportStore = new Map();

function roundViewport(pan, zoom) {
  return {
    pan: {
      x: Math.round(pan.x * 100) / 100,
      y: Math.round(pan.y * 100) / 100,
    },
    zoom: Math.round(zoom * 100) / 100,
  };
}

function saveViewport(tabId, campaignId, pan, zoom) {
  if (!tabId || !campaignId) return;
  const { pan: savedPan, zoom: savedZoom } = roundViewport(pan, zoom);
  viewportStore.set(tabId, { pan: savedPan, zoom: savedZoom, campaignId });
  return { pan: savedPan, zoom: savedZoom };
}

function getRestorableViewport(tabId, campaignId) {
  if (!tabId || !campaignId) return null;
  const saved = viewportStore.get(tabId);
  if (!saved || saved.campaignId !== campaignId) return null;
  return saved;
}

/**
 * Hook for managing viewport initialization and per-tab position memory
 *
 * APPROACH:
 * - Task positions are saved to the database, so they're stable
 * - On tab / campaign switch: restore that tab's viewport OR center
 * - Save viewport pan/zoom on every change (debounced, with stability check)
 * - Flush on unmount so a quick tab leave doesn't lose the last pan/zoom
 */
export function useViewportInitialization({
  campaignId,
  tasks,
  autoPositions, // Still needed for checking if positions are ready
  nodeDimensions, // Needed to wait for nodes to be measured
  containerRef, // Needed for calculating viewport dimensions
  pan,
  zoom,
  setPan,
  setZoom,
  handleGoToStart, // Only used for the animated Start button, not for initial positioning
  skipResizeAdjustment,
  isFocusMode = false, // When true, skip centering (FOKUS mode handles its own positioning)
}) {
  const { activeTabId } = useTabs();
  const tabId = activeTabId || null;

  const prevTabIdRef = useRef(null);
  const prevCampaignIdRef = useRef(null);
  // Tracks the campaignId we have finished centering/restoring for (under current tab)
  const hasCenteredRef = useRef(null);
  const [isViewReady, setIsViewReady] = useState(false);

  // Keep refs to latest values for saving on tab/campaign switch / unmount
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);
  const tabIdRef = useRef(tabId);
  const campaignIdRef = useRef(campaignId);

  // Track last saved values to avoid drift from micro-changes
  const lastSavedRef = useRef({ x: 0, y: 0, zoom: 1 });

  useLayoutEffect(() => {
    panRef.current = pan;
    zoomRef.current = zoom;
    tabIdRef.current = tabId;
    campaignIdRef.current = campaignId;
  }, [pan, zoom, tabId, campaignId]);

  const flushCurrentViewport = useCallback(() => {
    if (!tabIdRef.current || !campaignIdRef.current) return;
    if (hasCenteredRef.current !== campaignIdRef.current) return;
    const saved = saveViewport(
      tabIdRef.current,
      campaignIdRef.current,
      panRef.current,
      zoomRef.current,
    );
    if (saved) {
      lastSavedRef.current = { x: saved.pan.x, y: saved.pan.y, zoom: saved.zoom };
    }
  }, []);

  // Save viewport for current tab (debounced, with stability threshold)
  useEffect(() => {
    if (!tabId || !campaignId || hasCenteredRef.current !== campaignId) return;

    const lastSaved = lastSavedRef.current;
    const xDiff = Math.abs(pan.x - lastSaved.x);
    const yDiff = Math.abs(pan.y - lastSaved.y);
    const zoomDiff = Math.abs(zoom - lastSaved.zoom);

    if (xDiff < 1 && yDiff < 1 && zoomDiff < 0.01) return;

    const timer = setTimeout(() => {
      const saved = saveViewport(tabId, campaignId, pan, zoom);
      if (saved) {
        lastSavedRef.current = { x: saved.pan.x, y: saved.pan.y, zoom: saved.zoom };
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [tabId, campaignId, pan.x, pan.y, zoom]);

  // Flush when leaving flow view / unmounting so a quick tab switch isn't lost
  useEffect(() => () => flushCurrentViewport(), [flushCurrentViewport]);

  // Handle tab and/or campaign switching
  useLayoutEffect(() => {
    const prevTabId = prevTabIdRef.current;
    const prevCampaignId = prevCampaignIdRef.current;
    const tabChanged = tabId !== prevTabId;
    const campaignChanged = campaignId !== prevCampaignId;

    if (!tabChanged && !campaignChanged) return;

    skipResizeAdjustment?.(500);

    // Persist the viewport we are leaving (previous tab, or same tab new campaign)
    if (prevTabId && prevCampaignId && hasCenteredRef.current === prevCampaignId) {
      saveViewport(prevTabId, prevCampaignId, panRef.current, zoomRef.current);
    }

    hasCenteredRef.current = null;
    prevTabIdRef.current = tabId;
    prevCampaignIdRef.current = campaignId;

    if (!campaignId) {
      setIsViewReady(true);
      return;
    }

    // If FOKUS mode is active, DON'T restore saved viewport - let FOKUS handle positioning
    if (isFocusMode) {
      hasCenteredRef.current = campaignId;
      setIsViewReady(true);
      return;
    }

    const savedViewport = getRestorableViewport(tabId, campaignId);
    if (savedViewport) {
      setPan({ x: savedViewport.pan.x, y: savedViewport.pan.y });
      setZoom(savedViewport.zoom);
      lastSavedRef.current = {
        x: savedViewport.pan.x,
        y: savedViewport.pan.y,
        zoom: savedViewport.zoom,
      };
      hasCenteredRef.current = campaignId;
      setIsViewReady(true);
    } else {
      // No memory for this tab+campaign — reset; center when tasks/dimensions are ready
      setPan({ x: 0, y: 0 });
      setZoom(1);
      setIsViewReady(true);
      lastSavedRef.current = { x: 0, y: 0, zoom: 1 };
    }
  }, [tabId, campaignId, setPan, setZoom, skipResizeAdjustment, isFocusMode]);

  // Check if tasks have positions
  const allTasksHavePositions = tasks.length > 0 &&
    tasks.every(t => (t.x !== undefined && t.y !== undefined) || autoPositions[t.id]);

  // Check if nodes have been measured (needed for accurate centering)
  const allNodesHaveDimensions = tasks.length > 0 &&
    tasks.every(t => nodeDimensions[t.id]);

  // Helper to get position for a task
  const getTaskPosition = useCallback((task) => {
    if (autoPositions[task.id]) return autoPositions[task.id];
    if (task.x !== undefined && task.y !== undefined) return { x: task.x, y: task.y };
    return null;
  }, [autoPositions]);

  // Calculate the ideal start position (same logic as handleGoToStart but without animation)
  const calculateStartPosition = useCallback((containerWidth, containerHeight) => {
    const allPositions = tasks.map(task => getTaskPosition(task)).filter(Boolean);
    if (allPositions.length === 0) return { pan: { x: 0, y: 0 }, zoom: 1 };

    const targetZoom = 1;

    // Calculate bounding box using actual dimensions
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    tasks.forEach(task => {
      const pos = getTaskPosition(task);
      if (!pos) return;
      const dims = nodeDimensions[task.id] || { width: NODE_WIDTH, height: NODE_HEIGHT };
      minX = Math.min(minX, pos.x);
      minY = Math.min(minY, pos.y);
      maxX = Math.max(maxX, pos.x + dims.width);
      maxY = Math.max(maxY, pos.y + dims.height);
    });

    const contentWidth = (maxX - minX) * targetZoom;
    const contentHeight = (maxY - minY) * targetZoom;

    const edgePaddingX = Math.min(containerWidth * 0.1, 60);
    const edgePaddingY = Math.min(containerHeight * 0.1, 60);

    const fitsHorizontally = contentWidth + (edgePaddingX * 2) <= containerWidth;
    const fitsVertically = contentHeight + (edgePaddingY * 2) <= containerHeight;

    let targetPanX, targetPanY;

    if (fitsHorizontally) {
      const contentCenterX = (minX + maxX) / 2;
      targetPanX = (containerWidth / 2) - (contentCenterX * targetZoom);
    } else {
      // Find root tasks (no dependencies from either system)
      const rootTasks = tasks.filter(task => {
        const hasConditions = hasAnyConditions(task.unlockConditions);
        const hasDependsOn = task.dependsOn && task.dependsOn.length > 0;
        return !hasConditions && !hasDependsOn;
      });
      const rootPositions = rootTasks.map(task => getTaskPosition(task)).filter(Boolean);
      const targetPositions = rootPositions.length > 0 ? rootPositions : allPositions;

      const topRowTolerance = 20;
      const topRowPositions = targetPositions.filter(p => p.y <= minY + topRowTolerance);
      const topLeftPos = topRowPositions.reduce((leftmost, pos) =>
        pos.x < leftmost.x ? pos : leftmost
      , topRowPositions[0]);

      const leftPadding = Math.min(containerWidth * 0.3, 200);
      targetPanX = leftPadding - topLeftPos.x * targetZoom;
    }

    if (fitsVertically) {
      const contentCenterY = (minY + maxY) / 2;
      targetPanY = (containerHeight / 2) - (contentCenterY * targetZoom);
    } else {
      const topPadding = Math.min(containerHeight * 0.2, 100);
      targetPanY = topPadding - minY * targetZoom;
    }

    return { pan: { x: targetPanX, y: targetPanY }, zoom: targetZoom };
  }, [tasks, getTaskPosition, nodeDimensions]);

  // Center view when ready (only if not already centered/restored)
  useEffect(() => {
    // Already centered for this campaign (under current tab)
    if (hasCenteredRef.current === campaignId) return;

    // No campaign
    if (!campaignId) {
      hasCenteredRef.current = campaignId;
      return;
    }

    // Skip centering if FOKUS mode is active - it handles its own positioning
    if (isFocusMode) {
      hasCenteredRef.current = campaignId;
      return;
    }

    // Has saved viewport for this tab+campaign - already restored in the layout effect
    if (getRestorableViewport(tabId, campaignId)) {
      hasCenteredRef.current = campaignId;
      return;
    }

    // Waiting for tasks to load
    if (tasks.length === 0) return;

    // Waiting for positions
    if (!allTasksHavePositions) return;

    // Waiting for node dimensions to be measured
    if (!allNodesHaveDimensions) return;

    // Waiting for container to be mounted
    if (!containerRef?.current) return;

    // Calculate and set the position immediately (no animation)
    const containerRect = containerRef.current.getBoundingClientRect();
    const result = calculateStartPosition(containerRect.width, containerRect.height);

    setPan(result.pan);
    setZoom(result.zoom);
    lastSavedRef.current = { x: result.pan.x, y: result.pan.y, zoom: result.zoom };
    hasCenteredRef.current = campaignId;
  }, [tabId, campaignId, tasks.length, allTasksHavePositions, allNodesHaveDimensions, calculateStartPosition, containerRef, setPan, setZoom, isFocusMode]);

  return {
    isViewReady,
  };
}

export default useViewportInitialization;
